import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, RotateCcw, Calendar, StickyNote, FolderKanban, Tags, CheckSquare } from 'lucide-react';
import { apiGet, apiPatch } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { formatRelativeTime } from '../lib/relativeTime';

interface ArchivedItem {
  entity_type: 'meeting_series' | 'note' | 'scope' | 'tag' | 'task';
  id: string;
  name: string;
  archived_at: string;
}

const TYPE_LABELS: Record<string, string> = {
  meeting_series: 'Meeting Series',
  note: 'Note',
  scope: 'Project',
  tag: 'Tag',
  task: 'Task',
};

const TYPE_ICONS: Record<string, typeof Calendar> = {
  meeting_series: Calendar,
  note: StickyNote,
  scope: FolderKanban,
  tag: Tags,
  task: CheckSquare,
};

const TYPE_LINKS: Record<string, (id: string) => string> = {
  meeting_series: (id) => `/meetings/series/${id}`,
  note: (id) => `/notes/${id}`,
  scope: (id) => `/projects/${id}`,
  tag: () => '/tags',
  task: (id) => `/tasks/${id}`,
};

const FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'meeting_series', label: 'Meeting Series' },
  { value: 'note', label: 'Notes' },
  { value: 'scope', label: 'Projects' },
  { value: 'tag', label: 'Tags' },
  { value: 'task', label: 'Tasks' },
];

const UNARCHIVE_PATHS: Record<string, string> = {
  meeting_series: '/meeting-series',
  note: '/notes',
  scope: '/scopes',
  tag: '/tags',
  task: '/tasks',
};

export function ArchivePage() {
  const toast = useToast();
  const [items, setItems] = useState<ArchivedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  async function load() {
    try {
      const params = filter ? `?type=${filter}` : '';
      const data = await apiGet<ArchivedItem[]>(`/archive${params}`);
      setItems(data);
    } catch {
      toast.error('Failed to load archive');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [filter]);

  async function handleUnarchive(item: ArchivedItem) {
    try {
      await apiPatch(`${UNARCHIVE_PATHS[item.entity_type]}/${item.id}/unarchive`, {});
      toast.success(`${TYPE_LABELS[item.entity_type]} restored`);
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch {
      toast.error('Failed to restore item');
    }
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Archive size={22} className="text-text-muted" />
            <h1 className="text-xl font-bold text-text-primary">Archive</h1>
          </div>
          <div className="flex gap-1">
            {FILTER_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setFilter(opt.value)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                  filter === opt.value
                    ? 'bg-accent text-white'
                    : 'text-text-secondary hover:bg-bg-hover'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-text-muted text-sm">Loading...</div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <Archive size={40} className="mx-auto text-text-muted mb-3 opacity-40" />
            <p className="text-text-muted text-sm">
              {filter ? `No archived ${FILTER_OPTIONS.find(o => o.value === filter)?.label?.toLowerCase() || 'items'}` : 'Nothing archived yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {items.map(item => {
              const Icon = TYPE_ICONS[item.entity_type] || Archive;
              const link = TYPE_LINKS[item.entity_type]?.(item.id) || '#';
              return (
                <div
                  key={`${item.entity_type}-${item.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-bg-surface transition-colors group"
                >
                  <Icon size={16} className="text-text-muted flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Link
                      to={link}
                      className="text-sm font-medium text-text-primary hover:text-accent transition-colors truncate block"
                    >
                      {item.name}
                    </Link>
                    <div className="flex items-center gap-2 text-xs text-text-muted">
                      <span>{TYPE_LABELS[item.entity_type]}</span>
                      <span>archived {formatRelativeTime(item.archived_at)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleUnarchive(item)}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-text-secondary hover:text-accent hover:bg-bg-hover rounded-md transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <RotateCcw size={13} />
                    Restore
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
